import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { StatusBar } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Haptics } from '@capacitor/haptics';
import { Preferences } from '@capacitor/preferences';

/**
 * Singleton native API surface.
 *
 * Use `native.isNative` to gate any Capacitor-only code paths.
 * On web (jsdom, browser) all plugin calls are no-ops that return
 * safe defaults provided by the Capacitor web runtime.
 */
export const native = {
  isNative: Capacitor.isNativePlatform(),
  platform: Capacitor.getPlatform() as 'ios' | 'android' | 'web',
  app: App,
  browser: Browser,
  statusBar: StatusBar,
  splashScreen: SplashScreen,
  haptics: Haptics,
  preferences: Preferences,
};

/**
 * Open a URL correctly on both native and web platforms.
 *
 * - Native (iOS/Android): uses @capacitor/browser which opens an in-app
 *   Safari View Controller / Chrome Custom Tab. Required by Apple App Store
 *   review guidelines — apps must not open arbitrary URLs in the embedded WKWebView.
 * - Web: falls back to window.open with noopener/noreferrer.
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (native.isNative) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
